import {
  EMPTY_PLATFORM_APPEARANCE,
  activeFinishOf,
  appearanceOf,
  activeThemeOf,
  factoryChoiceOf,
  factoryThemeOf,
  finishCss,
  presentationOf,
  platformCssVars,
  themeCss,
  type FinishOptions,
  type PlatformAppearance,
  type PlatformSettings,
  type StyleChoice,
  type Presentation,
  type ThemeFile,
} from '@base/shared';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect } from 'react';
import { api } from './api.ts';
import { setSheet } from './css-sheet.ts';
import { PUBLIC_QUERY, useMe } from './auth.tsx';
import defaultLogoUrl from './assets/beadev-logo.png';
import defaultSymbolUrl from './assets/beadev-simbolo.png';
import visual from '../../../visual.json';
import { SISTEMA_NOME } from './sistema.ts';
import { shellOf, type ShellChoice } from './shell/choice.ts';
import { visitorStyle, withVisitorStyle } from './visitor-style.ts';

export const PLATFORM_KEY = ['platform', 'settings'];
/**
 * O visual do sistema (ADR 0135): o mesmo para todos, entrando ou não. Fica guardado ao entrar e
 * ao sair (a chave começa com PUBLIC_QUERY), para a moldura nunca voltar ao de fábrica no meio.
 */
export const APPEARANCE_KEY = [PUBLIC_QUERY, 'platform-appearance'];
/** O visual gravado, com o de passagem da vitrine por cima quando há um (ADR 0140). */
const fetchAppearance = async (signal?: AbortSignal) =>
  withVisitorStyle(
    await api.get<PlatformAppearance>('/platform/appearance', { signal }),
    visitorStyle(),
  );

/**
 * O visual que o sistema traz de fábrica: o visual.json da raiz do repositório, que o Smart Builder
 * preenche com o que o cliente escolheu: o estilo pronto pelo nome (`estilo`, ADR 0126) ou, nos
 * sistemas antigos, o tema copiado (`tema`, ADR 0117), mais o logo. Vazio (a base), vale o beadev.
 */
const built = visual as {
  estilo?: unknown;
  tema?: unknown;
  logo?: unknown;
  apresentacao?: unknown;
};
export const FACTORY_THEME: ThemeFile = factoryThemeOf(built, SISTEMA_NOME);
/** O estilo de fábrica (para a Configuração › Estilo abrir nele), ou null. */
export const FACTORY_STYLE: StyleChoice | null = factoryChoiceOf(built);

/**
 * O tema do sistema sem o montado do zero: o estilo escolhido na Configuração › Estilo ou, sem
 * ele, o de fábrica. É o ponto de partida do Guia visual e o "Voltar ao padrão" dele. Muda quando a
 * aparência gravada chega (`PlatformTheme`); por isso é `let` (as telas leem o valor atual).
 */
export let BASE_THEME: ThemeFile = FACTORY_THEME;
const builtLogo =
  typeof built.logo === 'string' && /^data:image\/(png|jpeg|webp);base64,/.test(built.logo)
    ? built.logo
    : null;
/** O logo do próprio cliente, sem cair no do beadev (a página de apresentação usa o nome no lugar). */
export const BUILT_LOGO_URL: string | null = builtLogo;
export const DEFAULT_LOGO_URL = builtLogo ?? defaultLogoUrl;
export const DEFAULT_SYMBOL_URL = builtLogo ?? defaultSymbolUrl;
/** A página de apresentação de fábrica (ADR 0118), ou null: sem ela, o endereço abre a tela de entrar. */
export const PRESENTATION: Presentation | null = presentationOf(built.apresentacao);

/** Aparência completa (Configuração › Plataforma), para as telas que a mudam. Só para quem entrou. */
export function usePlatformSettings() {
  const loggedIn = !!useMe().data;
  const query = useQuery({
    queryKey: PLATFORM_KEY,
    enabled: loggedIn,
    queryFn: () => api.get<PlatformSettings>('/platform/settings'),
    staleTime: 5 * 60_000,
  });
  return query;
}

/**
 * O visual que a moldura usa (ADR 0135). Já está no cache antes da primeira tela (`loadAppearance`);
 * sem ele (a API não respondeu a tempo), vale o de fábrica até chegar.
 */
function useAppearance(): PlatformAppearance | undefined {
  return useQuery({
    queryKey: APPEARANCE_KEY,
    queryFn: ({ signal }) => fetchAppearance(signal),
    staleTime: 5 * 60_000,
  }).data;
}

/** Quanto a primeira tela espera pelo visual antes de abrir com o de fábrica (API fora do ar). */
const APPEARANCE_WAIT_MS = 2500;

/**
 * Busca o visual gravado e o aplica ANTES da primeira tela (ADR 0135): sem isso, a página abria com
 * o visual de fábrica e trocava para o escolhido só depois de saber quem entrou. Chamado uma vez,
 * em main.tsx, antes do render. Nunca falha: sem resposta, a tela abre com o de fábrica.
 */
export async function loadAppearance(client: QueryClient): Promise<void> {
  try {
    const data = await fetchAppearance(AbortSignal.timeout(APPEARANCE_WAIT_MS));
    client.setQueryData(APPEARANCE_KEY, data);
  } catch {
    // sem resposta a tempo: abre com o de fábrica e o useAppearance tenta de novo
  }
  applyAppearance(client.getQueryData<PlatformAppearance>(APPEARANCE_KEY) ?? null);
}

/**
 * Mantém o visual igual ao gravado: depois de gravar na Configuração (a resposta completa chega no
 * PLATFORM_KEY), o visual da moldura acompanha na hora; e o `BASE_THEME` segue o estilo gravado,
 * antes de as telas renderizarem (o Guia visual abre nele). Ligado uma vez, em main.tsx.
 */
export function watchPlatformSettings(client: QueryClient) {
  const sync = () => {
    const data = client.getQueryData<PlatformAppearance>(APPEARANCE_KEY);
    BASE_THEME = data ? baseThemeOf(data) : FACTORY_THEME;
  };
  sync();
  return client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated') return;
    const key = event.query.queryKey;
    if (key[0] === PLATFORM_KEY[0] && key[1] === PLATFORM_KEY[1]) {
      const full = event.query.state.data as PlatformSettings | undefined;
      if (full) {
        client.setQueryData(APPEARANCE_KEY, withVisitorStyle(appearanceOf(full), visitorStyle()));
      }
    }
    if (key[0] === APPEARANCE_KEY[0] && key[1] === APPEARANCE_KEY[1]) sync();
  });
}

/** O tema do sistema sem o montado do zero: o estilo escolhido ou o de fábrica (ADR 0126). */
function baseThemeOf(s: Pick<PlatformAppearance, 'style'>): ThemeFile {
  return activeThemeOf({ theme: null, style: s.style }, FACTORY_THEME, SISTEMA_NOME);
}

/** Logo, símbolo e nome que a moldura mostra: o configurado ou o padrão da marca. */
export function usePlatformLook() {
  const s = useAppearance() ?? EMPTY_PLATFORM_APPEARANCE;
  return {
    logoUrl: s.logo ?? DEFAULT_LOGO_URL,
    symbolUrl: s.symbol ?? DEFAULT_SYMBOL_URL,
    appName: s.appName ?? SISTEMA_NOME,
  };
}

/** O tema que vale agora (ADR 0126): o do zero, o estilo escolhido ou o de fábrica. */
export function useActiveTheme(): ThemeFile {
  return activeThemeOf(useAppearance(), FACTORY_THEME, SISTEMA_NOME);
}

/** A moldura que vale agora (ADR 0128): o layout do estilo e o menu no celular. */
export function useActiveShell(): ShellChoice {
  const settings = useAppearance();
  const menu = activeThemeOf(settings, FACTORY_THEME, SISTEMA_NOME).menu;
  return shellOf(settings, FACTORY_STYLE, menu);
}

/** Acabamento que uma prévia põe por cima do que vale (o estúdio mostra o que está sendo escolhido). */
export const FinishPreviewContext = createContext<FinishOptions | null>(null);

/** O acabamento que vale agora (ADR 0128): os padrões de tela leem o vazio, o gráfico e o documento. */
export function useActiveFinish(): FinishOptions {
  const preview = useContext(FinishPreviewContext);
  const active = activeFinishOf(useAppearance(), FACTORY_STYLE);
  return preview ?? active;
}

/** Variáveis que a aparência configurada trocou (para devolver o padrão quando mudar de novo). */
let applied: string[] = [];

/** Aplica as cores da plataforma nas variáveis do styles.css (o tema inteiro segue). */
export function applyPlatformColors(
  s: Pick<PlatformSettings, 'accentColor' | 'paperColor' | 'inkColor'>,
) {
  const style = document.documentElement.style;
  for (const name of applied) style.removeProperty(name);
  const vars = platformCssVars(s);
  for (const [name, value] of Object.entries(vars)) style.setProperty(name, value);
  applied = Object.keys(vars);
}

/**
 * Aplica o tema do Guia visual (cores do claro e do escuro, medidas e estilo) numa folha de estilo
 * própria (construída pelo JavaScript, que o CSP de produção aceita: veja css-sheet.ts), por cima do styles.css. As três cores da Plataforma, quando escolhidas, ficam por cima
 * dele (applyPlatformColors). Sem tema gravado, vale o que o sistema traz (BASE_THEME). Também roda
 * ao abrir, antes de entrar: a tela de login já sai com o visual do sistema.
 */
export function applyTheme(theme: ThemeFile | null, finish?: FinishOptions) {
  const t = theme ?? BASE_THEME;
  setSheet('base-theme', themeCss(t));
  // O acabamento depois do tema: as variáveis dele (painel, botão secundário) valem por cima.
  const fallback = theme
    ? activeFinishOf({ theme, style: null }, null)
    : activeFinishOf(null, FACTORY_STYLE);
  setSheet('base-finish', finishCss(finish ?? fallback));
}

/** Aplica o visual inteiro: tema (ou o de fábrica), acabamento e as três cores da Plataforma. */
function applyAppearance(data: PlatformAppearance | null) {
  if (!data) return applyTheme(null);
  applyTheme(data.theme, activeFinishOf(data, FACTORY_STYLE));
  applyPlatformColors(data);
}

/** Liga o visual gravado a todas as telas (inclusive a de entrar) e o acompanha quando muda. */
export function PlatformTheme() {
  const data = useAppearance();
  useEffect(() => {
    if (data) applyAppearance(data);
  }, [data]);
  return null;
}
